import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

// Connects to data-controller="map"
export default class extends Controller {
  static targets = ["container", "centerButton"]
  static values = { 
    apiKey: String,
    centerLat: Number,
    centerLng: Number,
    zoom: Number,
    poiMode: Boolean
  }

  // Helper method to consistently check POI mode (handles string/boolean conversion)
  get isPoiMode() {
    return this.poiModeValue === true;
  }

  connect() {
    // Initialize the map with default location (will be updated when user location is found)
    this.map = L.map(this.containerTarget, {
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      dragging: true,
      touchZoom: true
    }).setView([this.centerLatValue || 37.7749, this.centerLngValue || -122.4194], this.zoomValue || 13)

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map)

    // Initialize markers arrays
    this.poiMarkers = []
    this.userLocationMarker = null
    
    // Initialize check-in radius properties
    this.checkInRadius = 100 // 100 meters default
    this.radarCircles = []
    this.radarAnimation = null
    this.isRadarActive = false

    // Initialize location tracking properties
    this.locationTrackingInterval = null
    this.accurateTrackingInterval = null
    this.isLocationTrackingActive = false
    this.trackingUpdateCount = 0

    // Initialize navigation state
    this.isNavigating = false
    this.navigationProfile = 'driving-car'
    this.navigationPoiId = null
    this.navigationPoiName = null
    this.navigationDestination = null // { lat, lng }
    this.navigationRouteCoords = [] // [[lat, lng], ...]
    this.navigationTotalDistance = 0 // meters
    this.navigationTotalDuration = 0 // seconds
    this.navigationRemainingDistance = 0 // meters
    this.navigationEtaSeconds = 0 // seconds
    this._lastRerouteAt = 0
    this._rerouteInFlight = false

    // Route planning (multi-POI)
    this.routePlanPois = [] // [{ id, name, lat, lng }]
    this.navigationWaypoints = null // ordered array of poi ids when following a plan
    this.currentWaypointIndex = null
    this.routePlanProfile = (window.localStorage && localStorage.getItem('route_plan_profile')) || 'driving-car'

    // First, determine if we are in POI mode and get the ID
    if (this.isPoiMode) {
      const urlParts = window.location.pathname.split('/');
      if (urlParts.includes('poi') && urlParts[urlParts.indexOf('poi') + 1]) {
        this.targetPoiId = urlParts[urlParts.indexOf('poi') + 1];
      }
    }

    // Load any persisted navigation state from cookie for restoration
    this._pendingNavigationState = this.loadNavigationStateFromCookie()

    // Now that targetPoiId is set, load the POIs
    this.loadPointsOfInterest();

    // Get user's current location
    if (!this.isPoiMode) {
      // In general map mode, suppress extra notification if we plan to auto-restore navigation
      if (this._pendingNavigationState) {
        this.getCurrentLocationSilently();
      } else {
        this.getCurrentLocation();
      }
    } else {
      // In POI mode, get location silently for radar functionality
      this.getCurrentLocationSilently();
    }

    // Set up event listeners
    this.setupEventListeners()

    // Start automatic location tracking after initial setup
    this.startLocationTracking()

    // Prepare navigation UI container
    this.ensureNavigationPanel()

    // Initialize storage for custom POIs
    this.customPoiMarkers = []
    // Enable map click to add custom POI to travel plan
    this.map.on('click', (e) => {
      const { lat, lng } = e.latlng
      this.showCustomPoiForm(lat, lng)
    })
  }

  disconnect() {
    // Clean up location tracking
    this.stopLocationTracking()
    
    // Clean up radar animation
    this.stopRadarAnimation()
    
    if (this.map) {
      this.map.remove()
    }
  }

  // Check-in Radius Radar Methods
  startRadarAnimation(lat, lng, radius = null) {
    this.stopRadarAnimation() // Stop any existing animation
    
    if (!lat || !lng) return
    
    this.isRadarActive = true
    this.radarCenter = [lat, lng]
    
    // Use provided radius or default check-in radius
    const maxRadius = radius || this.checkInRadius
    
    const animateRadar = () => {
      if (!this.isRadarActive) return
      
      // Create a new circle
      const circle = L.circle(this.radarCenter, {
        radius: 5, // Start small
        color: '#007bff',
        weight: 2,
        opacity: 0.8,
        fillColor: '#007bff',
        fillOpacity: 0.1,
        className: 'radar-circle'
      }).addTo(this.map)
      
      this.radarCircles.push(circle)
      
      // Animate the circle expansion
      let currentRadius = 5
      const radiusStep = maxRadius / 30 // 30 steps for smooth animation
      const opacityStep = 0.8 / 30
      
      const expandCircle = () => {
        if (!this.isRadarActive || !circle._map) return
        
        currentRadius += radiusStep
        const currentOpacity = Math.max(0, 0.8 - (currentRadius / maxRadius) * 0.8)
        const currentFillOpacity = Math.max(0, 0.1 - (currentRadius / maxRadius) * 0.1)
        
        circle.setRadius(currentRadius)
        circle.setStyle({
          opacity: currentOpacity,
          fillOpacity: currentFillOpacity
        })
        
        if (currentRadius >= maxRadius) {
          // Remove the circle when it reaches max radius
          this.map.removeLayer(circle)
          const index = this.radarCircles.indexOf(circle)
          if (index > -1) {
            this.radarCircles.splice(index, 1)
          }
        } else {
          setTimeout(expandCircle, 50) // 50ms for smooth animation
        }
      }
      
      expandCircle()
    }
    
    // Start the radar animation
    animateRadar()
    
    // Continue creating new circles every 800ms
    this.radarAnimation = setInterval(animateRadar, 800)
  }
  
  stopRadarAnimation() {
    this.isRadarActive = false
    
    if (this.radarAnimation) {
      clearInterval(this.radarAnimation)
      this.radarAnimation = null
    }
    
    // Remove all radar circles
    this.radarCircles.forEach(circle => {
      if (circle._map) {
        this.map.removeLayer(circle)
      }
    })
    this.radarCircles = []
  }
  
  showCheckInRadius(lat, lng, radius = null) {
    // Show a static radius circle for check-in area
    if (this.checkInRadiusCircle) {
      this.map.removeLayer(this.checkInRadiusCircle)
    }
    
    const radiusToUse = radius || this.checkInRadius
    
    this.checkInRadiusCircle = L.circle([lat, lng], {
      radius: radiusToUse,
      color: '#28a745',
      weight: 2,
      opacity: 0.6,
      fillColor: '#28a745',
      fillOpacity: 0.1,
      dashArray: '5, 5'
    }).addTo(this.map)
  }
  
  hideCheckInRadius() {
    if (this.checkInRadiusCircle) {
      this.map.removeLayer(this.checkInRadiusCircle)
      this.checkInRadiusCircle = null
    }
  }

  async loadPointsOfInterest() {
    try {
      let pois = [];
      if (this.isPoiMode && this.targetPoiId) {
        // In POI mode, fetch only the specific POI by its ID
        const response = await fetch(`/api/v1/point_of_interests/${this.targetPoiId}`);
        if (!response.ok) {
          throw new Error(`POI not found for ID: ${this.targetPoiId}`);
        }
        const data = await response.json();
        if (data.id) {
          pois = [data];
        }
      } else {
        // In normal mode, fetch all verified POIs plus user's private ones (backend includes both)
        const response = await fetch('/api/v1/point_of_interests');
        if (!response.ok) {
          throw new Error('Failed to fetch POIs');
        }
        const data = await response.json();
        pois = data.point_of_interests || [];
      }
      
      this.clearPoiMarkers();
      
      pois.forEach(poi => {
        this.addPoiMarker(poi);
      });
      
      // If in POI mode, highlight and open the specific POI
      if (this.isPoiMode && this.targetPoiId) {
        this.highlightTargetPoi(this.targetPoiId);
      }
    } catch (error) {
      console.error('Failed to load POIs:', error);
      this.showError('Failed to load locations. Please refresh the page.');
    }
  }

  addPoiMarker(poi) {
    if (!poi.coordinates || poi.coordinates.length !== 2) {
      console.warn('Invalid coordinates for POI:', poi)
      return
    }

    const [lng, lat] = poi.coordinates
    
    // Choose marker color based on trophy level, but highlight target POI
    const isTargetPoi = this.isPoiMode && this.targetPoiId === poi.id
    // Use same color as user's marker (blue) for user-private POIs
    let markerColor = poi.is_private ? 'blue' : this.getTrophyColor(poi.trophy_level, poi.checked_in)
    let iconSize = [32, 32]
    let iconClass = 'marker-icon'
    
    // Make target POI bigger and blue
    if (isTargetPoi) {
      markerColor = 'blue'
      iconSize = [40, 40]
      iconClass += ' target-poi'
    }
    
    // Create custom icon
    const customIcon = L.divIcon({
      className: `custom-marker ${markerColor}`,
      html: `<div class="${iconClass} ${markerColor}">
        <i class="bi bi-geo-alt-fill"></i>
      </div>`,
      iconSize: iconSize,
      iconAnchor: [iconSize[0]/2, iconSize[1]],
      popupAnchor: [0, -iconSize[1]]
    })

    const marker = L.marker([lat, lng], { icon: customIcon })
      .addTo(this.map)
      .bindPopup(this.createPopupContent(poi))

    // Add event listeners for radar animation
    marker.on('popupopen', () => {
      // Show radar animation centered on the POI when popup opens
      const poiRadius = poi.check_in_radius || 100
      this.startRadarAnimation(lat, lng, poiRadius)
    })

    marker.on('popupclose', () => {
      // Stop radar animation when popup closes (unless in POI mode)
      if (!this.isPoiMode) {
        this.stopRadarAnimation()
      }
    })

    // Store reference to marker with POI data
    marker.poi = poi
    this.poiMarkers.push(marker)

    return marker
  }

  getTrophyColor(trophyLevel, checkedIn) {
    // If checked in, use green regardless of trophy level
    if (checkedIn) {
      return 'green'
    }
    
    // Otherwise, use trophy-based colors
    switch (trophyLevel) {
      case 'gold':
        return 'gold'
      case 'silver':
        return 'silver'
      case 'bronze':
        return 'bronze'
      default:
        return 'bronze' // Default to bronze
    }
  }

  createPopupContent(poi) {
    // Check if user is signed in (window.userSignedIn set by server)
    const isSignedIn = window.userSignedIn || false

    let actionButton
    if (!isSignedIn) {
      actionButton = `<a href="/auth/google_oauth2" class="btn btn-outline-primary btn-sm">
        <i class="bi bi-box-arrow-in-right"></i> Sign In to Check In
      </a>`
    } else if (poi.checked_in) {
      actionButton = '<span class="badge bg-success"><i class="bi bi-check-circle"></i> Checked In</span>'
    } else {
      actionButton = `<button class="btn btn-primary btn-sm" onclick="window.mapController.attemptCheckIn('${poi.id}')">
        <i class="bi bi-geo"></i> Check In
      </button>`
    }

    // Trophy/custom display with check-in count
    let trophyDisplay = poi.trophy_icon ? 
      `<span style="font-size: 1.8em;" title="${poi.trophy_level} level">${poi.trophy_icon}</span>` : 
      `<span style="font-size: 1.8em;">⚪</span>`
    // For user-private POIs, override with a "Custom" badge
    if (poi.is_private) {
      trophyDisplay = `<span class="badge bg-secondary"><i class="bi bi-star-fill me-1"></i>Custom</span>`
    }
    
    // Add check-in count next to trophy for signed-in users
    if (isSignedIn && poi.user_check_ins_count > 0) {
      const visitText = poi.user_check_ins_count === 1 ? 'visit' : 'visits'
      trophyDisplay += `<span class="small text-muted ms-2">
        <i class="bi bi-arrow-repeat"></i> ${poi.user_check_ins_count} ${visitText}
      </span>`
    }

    // Delete button for user-private POIs (move to header top-right)
    const deleteBtn = (isSignedIn && poi.is_private) ? `<button class="btn btn-outline-danger btn-sm" title="Delete" onclick="window.mapController.deletePoi('${poi.id}')">
      <i class="bi bi-trash"></i>
    </button>` : ''

    return `
      <div class="poi-popup">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div class="d-flex align-items-center gap-2">
            <h6 class="mb-0">${poi.name}</h6>
            ${trophyDisplay}
          </div>
          ${deleteBtn}
        </div>
        <p class="mb-2 small text-muted">${poi.description}</p>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-muted">${poi.city}</small>
          ${actionButton}
        </div>
        ${isSignedIn ? `<div class="d-grid gap-2">
          <div class="btn-group btn-group-sm" role="group">
            <button class="btn btn-outline-info" onclick="window.mapController.navigateToPOI('${poi.id}', ${poi.coordinates[1]}, ${poi.coordinates[0]}, 'driving-car')" title="Drive">
              <i class="bi bi-car-front"></i>
            </button>
            <button class="btn btn-outline-info" onclick="window.mapController.navigateToPOI('${poi.id}', ${poi.coordinates[1]}, ${poi.coordinates[0]}, 'foot-walking')" title="Walk">
              <i class="bi bi-person-walking"></i>
            </button>
            <button class="btn btn-outline-info" onclick="window.mapController.navigateToPOI('${poi.id}', ${poi.coordinates[1]}, ${poi.coordinates[0]}, 'cycling-regular')" title="Cycle">
              <i class="bi bi-bicycle"></i>
            </button>
          </div>
          <button class="btn btn-outline-primary btn-sm" onclick="window.mapController.addPoiToRoute('${poi.id}')">
            <i class="bi bi-plus-circle"></i> Add to route
          </button>
        </div>` : ''}
      </div>
    `
  }

  async deletePoi(poiId) {
    try {
      const res = await fetch(`/api/v1/point_of_interests/${poiId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.success === false) {
        const msg = data && data.error ? data.error : 'Failed to delete'
        this.showError(msg)
        return
      }
      // Remove marker from map and internal arrays
      const idx = this.poiMarkers.findIndex(m => m.poi && m.poi.id === poiId)
      if (idx >= 0) {
        try { this.map.removeLayer(this.poiMarkers[idx]) } catch (_) {}
        this.poiMarkers.splice(idx, 1)
      }
      // Remove from route plan if present
      const before = this.routePlanPois.length
      this.routePlanPois = this.routePlanPois.filter(p => p.id !== poiId)
      if (this.routePlanPois.length < before) {
        this.updateNavigationPanel(true)
      }
      // Close any open popup
      try { this.map.closePopup() } catch (_) {}
      this.showSuccess('Place deleted')
    } catch (e) {
      this.showError('Failed to delete')
    }
  }

  clearPoiMarkers() {
    this.poiMarkers.forEach(marker => {
      this.map.removeLayer(marker)
    })
    this.poiMarkers = []
  }

  getCurrentLocationSilently() {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.')
      return
    }

    // Don't show notification in POI mode - get location silently for check-in functionality
    this.requestUserLocation(false)
  }

  getCurrentLocation() {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.')
      return
    }

    // Get location without showing initial loading message
    this.requestUserLocation(true)
  }

  requestUserLocation(showNotifications = true) {

    const options = {
      enableHighAccuracy: true,
      timeout: 15000, // Increased timeout for better accuracy
      maximumAge: 60000 // Cache for 1 minute
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.updateUserLocation(position.coords.latitude, position.coords.longitude, showNotifications)
      },
      (error) => {
        console.warn('Error getting location:', error.message)

        if (showNotifications) {
          // Show user-friendly error messages only if notifications are enabled
          let errorMessage = 'Could not get your location. '
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Please enable location permissions and refresh the page.'
              break
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'Location information unavailable.'
              break
            case error.TIMEOUT:
              errorMessage += 'Location request timed out.'
              break
            default:
              errorMessage += 'An unknown error occurred.'
              break
          }

          this.showNotification(errorMessage, 'warning')
        }
      },
      options
    )
  }

  updateUserLocation(lat, lng, showNotifications = true, trackingType = 'manual') {
    // Check if the popup was open before removing the marker
    let popupWasOpen = false
    if (this.userLocationMarker) {
      popupWasOpen = this.userLocationMarker.isPopupOpen()
      this.map.removeLayer(this.userLocationMarker)
    }

    // Store user coordinates
    this.userLatitude = lat
    this.userLongitude = lng

    // Check if user is within any POI radius and get the closest POI info
    const proximityResult = this.checkUserProximityToPOIs(lat, lng)
    const isInsidePOI = proximityResult.isInside
    const markerClass = isInsidePOI ? 'green' : 'blue'
    
    // Enhanced debug logging with tracking type
    const trackingIcon = {
      'fast': '⚡',
      'accurate': '🎯', 
      'manual': '👆',
      'checkin': '✅'
    }[trackingType] || '📍'
    
    if (isInsidePOI) {
      const poiNames = proximityResult.nearbyPOIs.map(p => `${p.poi.name} (${p.distance}m)`).join(', ')
      console.log(`${trackingIcon} ${trackingType.toUpperCase()} update: ${lat.toFixed(6)}, ${lng.toFixed(6)}. Inside ${proximityResult.nearbyPOIs.length} POI(s): ${poiNames}`)
    } else {
      console.log(`${trackingIcon} ${trackingType.toUpperCase()} update: ${lat.toFixed(6)}, ${lng.toFixed(6)}. Outside all POIs`)
    }
    
    // Create user location icon with dynamic color (same as POI checked-in color)
    const userIcon = L.divIcon({
      className: `user-location-marker ${markerClass}`,
      html: `<div class="user-location-icon">
        <i class="bi bi-person-fill"></i>
      </div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    })

    // Add new user location marker with dynamic popup content
    const popupContent = this.createUserLocationPopup(proximityResult)
    this.userLocationMarker = L.marker([lat, lng], { icon: userIcon })
      .addTo(this.map)
      .bindPopup(popupContent)

    // If the popup was open before, open it again on the new marker
    if (popupWasOpen) {
      this.userLocationMarker.openPopup()
    }

    // Only show radar if in POI mode (specific POI view)
    if (this.isPoiMode && this.targetPoiId) {
      // Find the target POI to get its location and radius
      const targetPoi = this.poiMarkers.find(marker => marker.poi && marker.poi.id === this.targetPoiId)?.poi
      if (targetPoi && targetPoi.coordinates) {
        const [poiLng, poiLat] = targetPoi.coordinates
        const poiRadius = targetPoi.check_in_radius || 100
        this.startRadarAnimation(poiLat, poiLng, poiRadius)
      }
    }

    // Only center the map on user's location if showNotifications is true
    // This prevents automatic centering during background location updates
    if (showNotifications) {
      if (!this.isPoiMode) {
        this.map.setView([lat, lng], 15, {
          animate: true,
          duration: 1.0
        })
        this.showSuccess('Found your location! Check-in radar is now active.')
      } else {
        // In POI mode, only center if explicitly requested (like center button click)
        this.showNotification('Your location has been marked on the map with check-in radius', 'info')
      }
    }

    // If a navigation session is active, update remaining route in real time
    if (this.isNavigating && Array.isArray(this.navigationRouteCoords) && this.navigationRouteCoords.length > 1) {
      this.handleNavigationUpdate(lat, lng)
      this.updateNavigationPanel()
    }

    // If we have a persisted navigation state and not currently navigating, try restoring now
    if (this._pendingNavigationState && !this.isNavigating && this.userLocationMarker) {
      const state = this._pendingNavigationState
      this._pendingNavigationState = null
      try {
        this.navigateToPOI(state.poiId, state.destination.lat, state.destination.lng, state.profile, state.poiName)
      } catch (_) {
        // If restore fails for any reason, clear cookie to avoid loops
        this.clearNavigationCookie()
      }
    }
  }

  centerOnUser() {
    if (this.userLatitude && this.userLongitude) {
      // If we already have the location, center on it immediately.
      this.map.setView([this.userLatitude, this.userLongitude], 15, {
        animate: true,
        duration: 1.0
      });
      this.showNotification('Centered on your location', 'info');
    } else {
      // If we don't have the location, get it first.
      // The updateUserLocation function will handle the centering.
      this.getCurrentLocation();
    }
  }

  // Helper method to check if user is within any POI's check-in radius
  checkUserProximityToPOIs(userLat, userLng) {
    if (!this.poiMarkers || this.poiMarkers.length === 0) {
      return { isInside: false, nearbyPOIs: [] }
    }

    const nearbyPOIs = []

    for (const marker of this.poiMarkers) {
      if (!marker.poi || !marker.poi.coordinates) {
        continue
      }

      const [poiLng, poiLat] = marker.poi.coordinates
      const poiRadius = marker.poi.check_in_radius || 100
      const distance = this.calculateDistance(userLat, userLng, poiLat, poiLng)
      
      // If user is within this POI's radius, add to nearby list
      if (distance <= poiRadius) {
        nearbyPOIs.push({
          poi: marker.poi,
          distance: Math.round(distance)
        })
      }
    }

    // Sort by distance (closest first)
    nearbyPOIs.sort((a, b) => a.distance - b.distance)

    return { 
      isInside: nearbyPOIs.length > 0, 
      nearbyPOIs: nearbyPOIs 
    }
  }

  // Calculate distance between two points in meters using Haversine formula
  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371e3 // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180 // φ, λ in radians
    const φ2 = lat2 * Math.PI/180
    const Δφ = (lat2-lat1) * Math.PI/180
    const Δλ = (lng2-lng1) * Math.PI/180

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))

    const d = R * c // in metres
    return d
  }

  // Create popup content for user location marker
  createUserLocationPopup(proximityResult) {
    if (!proximityResult.isInside || proximityResult.nearbyPOIs.length === 0) {
      return 'Your Location'
    }

    const isSignedIn = window.userSignedIn || false
    let content = '<div class="user-location-popup"><h6 class="mb-2">Your Location</h6>'
    
    if (proximityResult.nearbyPOIs.length === 1) {
      const nearby = proximityResult.nearbyPOIs[0]
      content += `<p class="mb-1 small"><i class="bi bi-geo-alt-fill text-success me-1"></i>Near <strong>${nearby.poi.name}</strong></p>`
      content += `<p class="mb-0 small text-muted">${nearby.distance}m away</p>`
    } else {
      content += `<p class="mb-2 small"><i class="bi bi-geo-alt-fill text-success me-1"></i>Multiple locations nearby:</p>`
      content += '<ul class="list-unstyled mb-0 small">'
      
      proximityResult.nearbyPOIs.forEach(nearby => {
        const statusIcon = nearby.poi.checked_in ? '<i class="bi bi-check-circle-fill text-success me-1"></i>' : '<i class="bi bi-circle me-1"></i>'
        content += `<li class="mb-1">${statusIcon}<strong>${nearby.poi.name}</strong> <span class="text-muted">(${nearby.distance}m)</span></li>`
      })
      
      content += '</ul>'
    }

    if (isSignedIn) {
      content += '<hr class="my-2">'
      content += '<p class="mb-0 small text-success"><i class="bi bi-info-circle me-1"></i>Ready to check in!</p>'
    }
    
    content += '</div>'
    return content
  }

  // Hybrid location tracking system
  startLocationTracking() {
    if (this.isLocationTrackingActive) {
      return // Already tracking
    }
    
    this.isLocationTrackingActive = true
    this.trackingUpdateCount = 0
    
    // Fast updates every 2 seconds with lower accuracy (Wi-Fi/Cell)
    this.locationTrackingInterval = setInterval(() => {
      this.trackingUpdateCount++
      this.updateUserLocationFast()
    }, 2000)
    
    // Accurate corrections every 15 seconds with GPS
    this.accurateTrackingInterval = setInterval(() => {
      this.updateUserLocationAccurate()
    }, 15000)
    
    console.log('📍 Hybrid location tracking started: Fast updates every 2s, GPS corrections every 15s')
  }

  stopLocationTracking() {
    if (this.locationTrackingInterval) {
      clearInterval(this.locationTrackingInterval)
      this.locationTrackingInterval = null
    }
    
    if (this.accurateTrackingInterval) {
      clearInterval(this.accurateTrackingInterval)
      this.accurateTrackingInterval = null
    }
    
    this.isLocationTrackingActive = false
    console.log('📍 Hybrid location tracking stopped')
  }

  // Fast updates using Wi-Fi/Cell (every 2 seconds)
  updateUserLocationFast() {
    if (!navigator.geolocation) {
      return
    }

    const options = {
      enableHighAccuracy: false, // Use Wi-Fi/Cell towers for speed
      timeout: 3000,             // Quick timeout for fast updates
      maximumAge: 5000           // Allow 5-second cache for responsive updates
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Update location silently without notifications
        this.updateUserLocation(position.coords.latitude, position.coords.longitude, false, 'fast')
      },
      (error) => {
        // Silently handle errors for fast updates
        console.warn(`⚡ Fast location update failed (${this.trackingUpdateCount}):`, error.message)
      },
      options
    )
  }

  // Accurate GPS corrections (every 15 seconds)
  updateUserLocationAccurate() {
    if (!navigator.geolocation) {
      return
    }

    const options = {
      enableHighAccuracy: true,  // Use GPS for maximum accuracy
      timeout: 12000,            // Longer timeout for GPS acquisition
      maximumAge: 0              // Force fresh GPS reading
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Update location with GPS accuracy
        this.updateUserLocation(position.coords.latitude, position.coords.longitude, false, 'accurate')
      },
      (error) => {
        // Silently handle GPS errors but log them
        console.warn('🎯 Accurate GPS update failed:', error.message)
      },
      options
    )
  }

  async attemptCheckIn(poiId) {
    if (!this.userLatitude || !this.userLongitude) {
      this.showError('Unable to determine your location. Please enable location services and try again.')
      return
    }

    // Find the POI data to get its location and radius
    const poi = this.poiMarkers.find(marker => marker.poi && marker.poi.id === poiId)?.poi
    if (!poi || !poi.coordinates) {
      this.showError('Unable to find POI location data.')
      return
    }

    const [poiLng, poiLat] = poi.coordinates
    const poiRadius = poi.check_in_radius || 100

    // Show static check-in radius around POI during attempt
    this.stopRadarAnimation()
    this.showCheckInRadius(poiLat, poiLng, poiRadius)
    this.showNotification('Attempting check-in...', 'info')

    // Get fresh, high-accuracy location for check-in
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await fetch('/api/v1/check_ins', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            },
            body: JSON.stringify({
              point_of_interest_id: poiId,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            })
          })
          
          // Log the check-in coordinates for debugging
          console.log(`✅ CHECK-IN coordinates: ${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)} (accuracy: ${position.coords.accuracy?.toFixed(1)}m)`)

          const data = await response.json()

          if (data.success) {
            this.showSuccess(data.message)
            // Reload POIs to update marker colors
            this.loadPointsOfInterest()
            // Restart radar animation after successful check-in
            setTimeout(() => {
              this.hideCheckInRadius()
              this.startRadarAnimation(poiLat, poiLng, poiRadius)
            }, 2000)
          } else {
            this.showError(data.error)
            // Restart radar animation after failed check-in
            setTimeout(() => {
              this.hideCheckInRadius()
              this.startRadarAnimation(poiLat, poiLng, poiRadius)
            }, 1000)
          }
        } catch (error) {
          console.error('Check-in error:', error)
          this.showError('Failed to check in. Please try again.')
          // Restart radar animation after error
          setTimeout(() => {
            this.hideCheckInRadius()
            this.startRadarAnimation(poiLat, poiLng, poiRadius)
          }, 1000)
        }
      },
      (error) => {
        this.showError('Unable to get precise location for check-in. Please try again.')
        // Restart radar animation after location error
        setTimeout(() => {
          this.hideCheckInRadius()
          this.startRadarAnimation(poiLat, poiLng, poiRadius)
        }, 1000)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0 // Force fresh location
      }
    )
  }

  showSuccess(message) {
    this.showNotification(message, 'success')
  }

  showError(message) {
    this.showNotification(message, 'danger')
  }

  showNotification(message, type) {
    // Create Bootstrap alert
    const alertContainer = document.getElementById('alert-container') || this.createAlertContainer()
    
    // If this is a navigation message, collapse previous ones to avoid stacking
    if (message.includes('route to')) {
      this.dismissNotificationsMatching('info', 'route to')
      this.dismissNotificationsMatching('success', 'route to')
      this.dismissNotificationsMatching('warning', 'route to')
    }

    // Get appropriate icon for notification type
    const iconMap = {
      'success': 'bi-check-circle-fill',
      'info': 'bi-info-circle-fill', 
      'warning': 'bi-exclamation-triangle-fill',
      'danger': 'bi-x-circle-fill'
    }
    
    const icon = iconMap[type] || 'bi-info-circle-fill'
    
    const alert = document.createElement('div')
    alert.className = `alert alert-${type} alert-dismissible fade show`
    alert.innerHTML = `
      <button type="button" class="btn-close position-absolute" data-bs-dismiss="alert" aria-label="Close"></button>
      <div class="d-flex align-items-center">
        <i class="${icon} me-2"></i>
        <span>${message}</span>
      </div>
    `
    
    alertContainer.appendChild(alert)
    
    // Auto-remove after 4 seconds for better UX
    setTimeout(() => {
      if (alert.parentNode) {
        // Add fade out animation
        alert.style.animation = 'slideOutUp 0.3s ease-in forwards'
        setTimeout(() => {
          if (alert.parentNode) {
            alert.remove()
          }
        }, 300)
      }
    }, 4000)
  }

  // Utility to remove queued alerts matching a type and substring
  dismissNotificationsMatching(type, substring) {
    const alertContainer = document.getElementById('alert-container') || this.createAlertContainer()
    const selector = `.alert-${type}`
    const alerts = alertContainer.querySelectorAll(selector)
    alerts.forEach(alert => {
      if (alert.textContent && alert.textContent.includes(substring)) {
        try { alert.remove() } catch (_) {}
      }
    })
  }

  createAlertContainer() {
    const container = document.createElement('div')
    container.id = 'alert-container'
    container.className = 'position-fixed top-0 start-50 translate-middle-x'
    container.style.zIndex = '9999'
    container.style.marginTop = '0px' // Using CSS top positioning instead
    
    // On mobile, position notifications between header and controls
    if (window.innerWidth <= 768) {
      container.className = 'position-fixed'
      container.style.marginTop = '0'
      container.style.overflowY = 'auto'
      
      if (window.innerWidth <= 480) {
        // Very small screens - subtle spacing
        container.style.top = '60px'
        container.style.left = '5px'
        container.style.right = '5px'
        container.style.maxHeight = 'calc(100vh - 125px)'
      } else {
        // Regular mobile screens - subtle space below header
        container.style.top = '65px'
        container.style.left = '10px'
        container.style.right = '10px'
        container.style.maxHeight = 'calc(100vh - 135px)'
      }
    }
    
    document.body.appendChild(container)
    return container
  }

  // ---------- Route loading overlay (spinner) ----------
  ensureRouteLoadingOverlay() {
    if (document.getElementById('route-loading-overlay')) return
    const overlay = document.createElement('div')
    overlay.id = 'route-loading-overlay'
    overlay.style.position = 'fixed'
    overlay.style.left = '0'
    overlay.style.top = '0'
    overlay.style.right = '0'
    overlay.style.bottom = '0'
    overlay.style.display = 'none'
    overlay.style.alignItems = 'center'
    overlay.style.justifyContent = 'center'
    overlay.style.background = 'rgba(0,0,0,0.25)'
    overlay.style.zIndex = '10002'
    overlay.innerHTML = `
      <div class="card shadow-sm p-3" style="min-width:240px; border-radius:12px;">
        <div class="d-flex align-items-center gap-2">
          <div class="spinner-border text-primary" role="status" aria-label="Loading"></div>
          <div id="route-loading-text" class="fw-semibold">Calculating route…</div>
        </div>
      </div>
    `
    document.body.appendChild(overlay)
  }

  showRouteLoading(message = 'Calculating route…') {
    this.ensureRouteLoadingOverlay()
    const overlay = document.getElementById('route-loading-overlay')
    const textEl = overlay?.querySelector('#route-loading-text')
    if (textEl) textEl.textContent = message
    if (overlay) overlay.style.display = 'flex'
  }

  hideRouteLoading() {
    const overlay = document.getElementById('route-loading-overlay')
    if (overlay) overlay.style.display = 'none'
  }

  highlightTargetPoi(poiId) {
    // Find the marker for the target POI and open its popup
    const targetMarker = this.poiMarkers.find(marker => marker.poi && marker.poi.id === poiId)
    if (targetMarker) {
      // Open the popup for the target POI
      targetMarker.openPopup()
      
      // Add a subtle bounce animation
      setTimeout(() => {
        const markerElement = targetMarker.getElement()
        if (markerElement) {
          markerElement.style.animation = 'bounce 0.6s ease-in-out'
          setTimeout(() => {
            markerElement.style.animation = ''
          }, 600)
        }
      }, 500)
    }
  }

  async navigateToPOI(poiId, poiLat, poiLng, profile = 'driving-car', poiNameOverride = null) {
    // Check if user location is available
    if (!this.userLocationMarker) {
      this.showNotification('Please get your current location first. Click the "Find Me" button.', 'warning')
      return
    }

    const userLat = this.userLocationMarker.getLatLng().lat
    const userLng = this.userLocationMarker.getLatLng().lng

    // Determine POI name from override or marker data
    const poiMarker = this.poiMarkers.find(marker => marker.poi && marker.poi.id === poiId)
    const poiName = poiNameOverride || (poiMarker ? poiMarker.poi.name : 'Destination')
    
    
    // Get transport mode display name
    const transportMode = {
      'driving-car': '🚗 Driving',
      'foot-walking': '🚶 Walking',
      'cycling-regular': '🚴 Cycling'
    }[profile] || '🚗 Driving'
    
    // Show loading notification and spinner
    this.showNotification(`${transportMode} route to ${poiName}...`, 'info')
    this.showRouteLoading(`Calculating ${transportMode.replace(/^[^ ]+ /, '')} route…`)
    
    try {
      // Close any existing info/success navigation notifications before showing a new one
      this.dismissNotificationsMatching('info', `${transportMode} route to`)

      // Get real route from OpenRouteService API
      const response = await fetch('/api/routes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({
          start: [userLng, userLat], // OpenRouteService expects [lng, lat]
          end: [poiLng, poiLat],
          profile: profile // Can be: driving-car, foot-walking, cycling-regular
        })
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Failed to get route')
      
      if (data.success) {
        // Set navigation session state
        this.isNavigating = true
        this.navigationProfile = profile
        this.navigationPoiId = poiId
        this.navigationPoiName = poiName
        this.navigationDestination = { lat: poiLat, lng: poiLng }
        this.navigationTotalDistance = data.distance
        this.navigationTotalDuration = data.duration

        // Hide spinner right before drawing the route
        this.hideRouteLoading()
        this.drawRealRoute(data.route, poiName, data.distance, data.duration, profile)

        // Persist navigation state so we can restore after reload/tab close
        this.persistNavigationStateToCookie()
        
        // Replace the in-flight info with a single success notification
        this.dismissNotificationsMatching('info', `${transportMode} route to`)
        this.dismissNotificationsMatching('success', `${transportMode} route to`)
        const distanceKm = (data.distance / 1000).toFixed(1)
        const durationMin = Math.round(data.duration / 60)
        this.showNotification(`${transportMode} route to ${poiName}: ${distanceKm}km, ~${durationMin}min`, 'success')
        
        // Center map on route
        const bounds = L.latLngBounds([[userLat, userLng], [poiLat, poiLng]])
        this.map.fitBounds(bounds, { padding: [50, 50] })

        // Prime remaining route immediately based on current position
        this.handleNavigationUpdate(userLat, userLng)
        this.updateNavigationPanel(true)
      } else {
        throw new Error(data.error || 'Failed to get route')
      }
    } catch (error) {
      // Ensure spinner is hidden before drawing fallback
      this.hideRouteLoading()
      let errorMessage = 'Route calculation failed'
      if (error.message.includes('Failed to get route')) {
        errorMessage = 'OpenRouteService API error - check API key and endpoint'
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error - check internet connection'
      }
      
      this.dismissNotificationsMatching('info', `${transportMode} route to`)
      this.dismissNotificationsMatching('success', `${transportMode} route to`)
      this.showNotification(`${transportMode} ${errorMessage}. Using direct path to ${poiName}.`, 'warning')
      
      // Fallback to simple direct route
      const distance = this.calculateDistance(userLat, userLng, poiLat, poiLng)
      this.drawNavigationRoute(userLat, userLng, poiLat, poiLng)
      this.dismissNotificationsMatching('info', `${transportMode} route to`)
      this.showNotification(`${transportMode} direct path to ${poiName}: ${this.formatDistance(distance)}`, 'info')

      // Start a navigation session even with direct path so we can trim remaining route
      this.isNavigating = true
      this.navigationProfile = profile
      this.navigationPoiId = poiId
      this.navigationPoiName = poiName
      this.navigationDestination = { lat: poiLat, lng: poiLng }
      this.navigationTotalDistance = distance
      this.navigationTotalDuration = 0
      this.navigationRouteCoords = [
        [userLat, userLng],
        [poiLat, poiLng]
      ]

      // Persist navigation state for restoration
      this.persistNavigationStateToCookie()
      this.handleNavigationUpdate(userLat, userLng)
      this.updateNavigationPanel(true)
    } finally {
      // Safety: ensure spinner is hidden
      this.hideRouteLoading()
    }
  }

  drawNavigationRoute(startLat, startLng, endLat, endLng) {
    // Remove existing route if any
    if (this.navigationRoute) {
      this.map.removeLayer(this.navigationRoute)
    }

    // Create route line
    this.navigationRoute = L.polyline([[startLat, startLng], [endLat, endLng]], {
      color: '#007bff',
      weight: 4,
      opacity: 0.8,
      dashArray: '10, 5'
    }).addTo(this.map)

    // Add start and end markers
    if (this.navigationStartMarker) {
      this.map.removeLayer(this.navigationStartMarker)
    }
    if (this.navigationEndMarker) {
      this.map.removeLayer(this.navigationEndMarker)
    }

    this.navigationStartMarker = L.marker([startLat, startLng], {
      icon: L.divIcon({
        className: 'navigation-start-marker',
        html: '<div class="navigation-start-icon">🚀</div>',
        iconSize: [24, 24]
      })
    }).addTo(this.map)

    this.navigationEndMarker = L.marker([endLat, endLng], {
      icon: L.divIcon({
        className: 'navigation-end-marker',
        html: '<div class="navigation-end-icon">🎯</div>',
        iconSize: [24, 24]
      })
    }).addTo(this.map)
  }

  drawRealRoute(routeData, poiName, distance, duration, profile = 'driving-car') {
    // Remove existing route if any
    if (this.navigationRoute) {
      this.map.removeLayer(this.navigationRoute)
    }
    if (this.navigationStartMarker) {
      this.map.removeLayer(this.navigationStartMarker)
    }
    if (this.navigationEndMarker) {
      this.map.removeLayer(this.navigationEndMarker)
    }

    try {
      // Try to get real road coordinates from OpenRouteService API response
      let latLngCoords = []
      
      // Prefer GeoJSON from backend
      if (routeData && routeData.features && routeData.features[0] && routeData.features[0].geometry) {
        const geo = routeData.features[0].geometry
        if (geo && geo.type === 'LineString' && Array.isArray(geo.coordinates)) {
          latLngCoords = geo.coordinates.map(([lng, lat]) => [lat, lng])
        }
      }
      
      // Fallback to encoded polyline structure
      if (latLngCoords.length === 0 && routeData.routes && routeData.routes[0] && routeData.routes[0].geometry) {
        const encodedPolyline = routeData.routes[0].geometry
        const decoded = this.decodePolylineAuto(encodedPolyline)
        latLngCoords = decoded.map(([lng, lat]) => [lat, lng])
      }
      
      // Fallback to simple route if polyline decoding fails
      if (latLngCoords.length === 0) {
        const startLat = this.userLocationMarker.getLatLng().lat
        const startLng = this.userLocationMarker.getLatLng().lng
        const poiMarker = this.poiMarkers.find(marker => marker.poi && marker.poi.name === poiName)
        if (poiMarker) {
          const endLat = poiMarker.getLatLng().lat
          const endLng = poiMarker.getLatLng().lng
          latLngCoords = this.createCurvedRoute(startLat, startLng, endLat, endLng)
        }
      }

      // Draw the actual road route
      const validCoords = latLngCoords.filter(coord =>
        Array.isArray(coord) &&
        coord.length === 2 &&
        typeof coord[0] === 'number' &&
        typeof coord[1] === 'number' &&
        coord[0] >= -90 && coord[0] <= 90 &&
        coord[1] >= -180 && coord[1] <= 180
      )

      if (validCoords.length > 0) {
        // Persist full route coordinates for navigation trimming
        this.navigationRouteCoords = validCoords
        this.navigationRoute = L.polyline(validCoords, {
          color: '#28a745',
          weight: 6,
          opacity: 0.9,
          smoothFactor: 1
        })
        this.navigationRoute.addTo(this.map)
        this.map.invalidateSize()
      } else {
        throw new Error('No valid coordinates')
      }

      // Add start marker (user location)
      const startCoords = latLngCoords[0]
      this.navigationStartMarker = L.marker(startCoords, {
        icon: L.divIcon({
          className: 'navigation-start-marker',
          html: '<div class="navigation-start-icon">🚀</div>',
          iconSize: [24, 24]
        })
      }).addTo(this.map)

      // Add end marker (POI)
      const endCoords = latLngCoords[latLngCoords.length - 1]
      this.navigationEndMarker = L.marker(endCoords, {
        icon: L.divIcon({
          className: 'navigation-end-marker',
          html: '<div class="navigation-end-icon">🎯</div>',
          iconSize: [24, 24]
        })
      }).addTo(this.map)

      // Get transport mode icon and name
      const transportInfo = {
        'driving-car': { icon: '🚗', name: 'Driving' },
        'foot-walking': { icon: '🚶', name: 'Walking' },
        'cycling-regular': { icon: '🚴', name: 'Cycling' }
      }[profile] || { icon: '🚗', name: 'Driving' }
      
      // Add route info popup
      const routeInfo = L.popup({
        className: 'route-info-popup',
        closeButton: true,
        maxWidth: 300
      })
        .setLatLng(endCoords)
        .setContent(`
          <div class="text-center">
            <h6 class="mb-2">${transportInfo.icon} ${transportInfo.name} Route to ${poiName}</h6>
            <div class="row text-muted">
              <div class="col-6">
                <i class="bi bi-arrow-right"></i> ${(distance / 1000).toFixed(1)} km
              </div>
              <div class="col-6">
                <i class="bi bi-clock"></i> ~${Math.round(duration / 60)} min
              </div>
            </div>
          </div>
        `)
        .openOn(this.map)

      // Auto-close popup after 5 seconds
      setTimeout(() => {
        if (routeInfo.isOpen()) {
          routeInfo.close()
        }
      }, 5000)

      // Center map on the route
      if (latLngCoords.length > 0) {
        const bounds = L.latLngBounds(latLngCoords)
        this.map.fitBounds(bounds, { padding: [50, 50] })
      }

    } catch (error) {
      console.error('Error drawing real route:', error)
      // Fallback to simple route using user location and POI coordinates
      const userLat = this.userLocationMarker.getLatLng().lat
      const userLng = this.userLocationMarker.getLatLng().lng
      
      // Try to get POI coordinates from the route data or fallback to direct path
      if (routeData.routes && routeData.routes[0] && routeData.routes[0].bbox) {
        // Use the bounding box to get approximate end coordinates
        const bbox = routeData.routes[0].bbox
        const endLat = bbox[3] // max lat
        const endLng = bbox[2] // max lng
        this.drawNavigationRoute(userLat, userLng, endLat, endLng)
      } else {
        // Complete fallback - just draw a straight line
        this.drawNavigationRoute(userLat, userLng, userLat + 0.001, userLng + 0.001)
      }
    }
  }

  // Helper: extract [lat,lng] array from ORS response (geojson or json)
  extractRouteLatLng(routeData) {
    try {
      if (routeData && routeData.features && routeData.features[0] && routeData.features[0].geometry) {
        const geo = routeData.features[0].geometry
        if (geo && geo.type === 'LineString' && Array.isArray(geo.coordinates)) {
          return geo.coordinates.map(([lng, lat]) => [lat, lng])
        }
      }
      if (routeData && routeData.routes && routeData.routes[0] && routeData.routes[0].geometry) {
        const encoded = routeData.routes[0].geometry
        const decoded = this.decodePolylineAuto(encoded)
        return decoded.map(([lng, lat]) => [lat, lng])
      }
    } catch (_) {}
    return []
  }

  // Real-time navigation updater: trims displayed route to show only the remaining path
  handleNavigationUpdate(currentLat, currentLng) {
    if (!this.isNavigating || !this.navigationRoute || !Array.isArray(this.navigationRouteCoords) || this.navigationRouteCoords.length < 2) {
      return
    }

    // Find nearest point ON the route (project onto segments), not just a vertex
    const nearest = this.findNearestPointOnRoute(currentLat, currentLng, this.navigationRouteCoords)
    const nearestDistance = nearest.distanceMeters
    const nearestSegmentIndex = nearest.segmentIndex
    const projectedPoint = [nearest.lat, nearest.lng]

    // Determine off-route thresholds by profile
    const offRouteThresholdByProfile = {
      'foot-walking': 30,
      'cycling-regular': 60,
      'driving-car': 120
    }
    const threshold = offRouteThresholdByProfile[this.navigationProfile] || 100

    // If far from route, attempt reroute (debounced)
    if (nearestDistance > threshold) {
      this.rerouteFromHere(currentLat, currentLng)
      return
    }

    // If very close to destination, end navigation
    if (this.navigationDestination) {
      const distToDest = this.calculateDistance(currentLat, currentLng, this.navigationDestination.lat, this.navigationDestination.lng)
      if (distToDest <= 25) {
        // Multi-stop support: proceed to next waypoint when following a plan
        // In full-plan mode we keep a single polyline for the entire route, so we don't auto-switch legs here
        this.stopNavigation()
        this.showSuccess(`Arrived at ${this.navigationPoiName || 'destination'}`)
        return
      }
    }

    // Build remaining route: start at projected point on the route, then continue along route geometry
    const remaining = [projectedPoint]
    for (let j = nearestSegmentIndex + 1; j < this.navigationRouteCoords.length; j++) {
      remaining.push(this.navigationRouteCoords[j])
    }
    // Safety: ensure the destination is included
    if (this.navigationDestination) {
      const last = remaining[remaining.length - 1]
      if (!last || Math.abs(last[0] - this.navigationDestination.lat) > 1e-6 || Math.abs(last[1] - this.navigationDestination.lng) > 1e-6) {
        remaining.push([this.navigationDestination.lat, this.navigationDestination.lng])
      }
    }

    // Update polyline to show only remaining path
    try {
      this.navigationRoute.setLatLngs(remaining)
    } catch (_) {
      // If polyline was removed, re-add it
      this.navigationRoute = L.polyline(remaining, {
        color: '#28a745',
        weight: 6,
        opacity: 0.9,
        smoothFactor: 1
      }).addTo(this.map)
    }

    // Move start marker to current position during navigation
    if (this.navigationStartMarker) {
      try { this.navigationStartMarker.setLatLng([currentLat, currentLng]) } catch (_) {}
    }

    // Update remaining distance along the route from projection point
    let remainingDistance = 0
    if (nearestSegmentIndex >= 0 && nearestSegmentIndex < this.navigationRouteCoords.length - 1) {
      const [segNextLat, segNextLng] = this.navigationRouteCoords[nearestSegmentIndex + 1]
      remainingDistance += this.calculateDistance(projectedPoint[0], projectedPoint[1], segNextLat, segNextLng)
      for (let k = nearestSegmentIndex + 1; k < this.navigationRouteCoords.length - 1; k++) {
        const [aLat, aLng] = this.navigationRouteCoords[k]
        const [bLat, bLng] = this.navigationRouteCoords[k + 1]
        remainingDistance += this.calculateDistance(aLat, aLng, bLat, bLng)
      }
    }

    this.navigationRemainingDistance = remainingDistance
    if (this.navigationTotalDistance > 0 && this.navigationTotalDuration > 0) {
      const avgSpeedMps = this.navigationTotalDistance / this.navigationTotalDuration
      this.navigationEtaSeconds = Math.max(10, Math.round(remainingDistance / avgSpeedMps))
    }
  }

  // Compute nearest point on a polyline to current location using simple equirectangular projection
  findNearestPointOnRoute(currentLat, currentLng, routeCoords) {
    let best = { segmentIndex: 0, lat: routeCoords[0][0], lng: routeCoords[0][1], distanceMeters: this.calculateDistance(currentLat, currentLng, routeCoords[0][0], routeCoords[0][1]) }
    for (let i = 0; i < routeCoords.length - 1; i++) {
      const a = { lat: routeCoords[i][0], lng: routeCoords[i][1] }
      const b = { lat: routeCoords[i + 1][0], lng: routeCoords[i + 1][1] }
      const proj = this.projectPointToSegment(currentLat, currentLng, a.lat, a.lng, b.lat, b.lng)
      if (proj.distanceMeters < best.distanceMeters) {
        best = { segmentIndex: i, lat: proj.lat, lng: proj.lng, distanceMeters: proj.distanceMeters }
      }
    }
    return best
  }

  projectPointToSegment(pLat, pLng, aLat, aLng, bLat, bLng) {
    const R = 6371000
    const refLatRad = pLat * Math.PI / 180
    const toXY = (lat, lng) => {
      const x = (lng - pLng) * Math.PI / 180 * Math.cos(refLatRad) * R
      const y = (lat - pLat) * Math.PI / 180 * R
      return { x, y }
    }
    const fromXY = (x, y) => {
      const lat = (y / R) * 180 / Math.PI + pLat
      const lng = (x / (R * Math.cos(refLatRad))) * 180 / Math.PI + pLng
      return { lat, lng }
    }

    const P = { x: 0, y: 0 }
    const A = toXY(aLat, aLng)
    const B = toXY(bLat, bLng)
    const AB = { x: B.x - A.x, y: B.y - A.y }
    const AP = { x: P.x - A.x, y: P.y - A.y }
    const ab2 = AB.x * AB.x + AB.y * AB.y
    let t = ab2 > 0 ? (AP.x * AB.x + AP.y * AB.y) / ab2 : 0
    t = Math.max(0, Math.min(1, t))
    const proj = { x: A.x + AB.x * t, y: A.y + AB.y * t }
    const projected = fromXY(proj.x, proj.y)
    const distanceMeters = this.calculateDistance(pLat, pLng, projected.lat, projected.lng)
    return { lat: projected.lat, lng: projected.lng, distanceMeters }
  }

  // ---------- Navigation Panel UI ----------
  ensureNavigationPanel() {
    if (document.getElementById('navigation-panel')) return
    const panel = document.createElement('div')
    panel.id = 'navigation-panel'
    panel.style.position = 'fixed'
    panel.style.bottom = '16px'
    panel.style.left = '50%'
    panel.style.transform = 'translateX(-50%)'
    panel.style.zIndex = '10000'
    panel.style.background = 'rgba(255,255,255,0.95)'
    panel.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)'
    panel.style.borderRadius = '12px'
    panel.style.padding = '8px 12px'
    panel.style.minWidth = '260px'
    panel.style.maxWidth = '90vw'
    panel.style.display = 'none'
    panel.style.backdropFilter = 'blur(4px)'

    panel.innerHTML = `
      <div id="nav-content" style="display:flex; align-items:center; gap:10px;"></div>
    `

    document.body.appendChild(panel)
    // Actions are bound in updateNavigationPanel based on current state
  }

  updateNavigationPanel(forceShow = false) {
    this.ensureNavigationPanel()
    const panel = document.getElementById('navigation-panel')
    if (!panel) return

    const content = panel.querySelector('#nav-content')
    if (!content) return

    // If navigating, show status
    if (this.isNavigating) {
      const transportInfo = {
        'driving-car': { icon: '🚗', name: 'Driving' },
        'foot-walking': { icon: '🚶', name: 'Walking' },
        'cycling-regular': { icon: '🚴', name: 'Cycling' }
      }[this.navigationProfile] || { icon: '🚗', name: 'Driving' }

      const dist = this.navigationRemainingDistance || this.navigationTotalDistance || 0
      const etaSec = this.navigationEtaSeconds || (this.navigationTotalDuration || 0)
      const distStr = dist < 1000 ? `${Math.max(0, Math.round(dist))} m` : `${(dist / 1000).toFixed(1)} km`
      const etaStr = etaSec > 0 ? `~${Math.max(1, Math.round(etaSec / 60))} min` : '--'
      // For full-plan navigation, we still show remaining count; no Next button needed
      const hasNext = false
      const remainingStops = Array.isArray(this.navigationWaypoints) ? Math.max(0, this.navigationWaypoints.length - 1) : 0
      const showDetails = Array.isArray(this.navigationWaypoints) && this.navigationWaypoints.length > 0

      content.innerHTML = `
        <div id="nav-mode-icon" style="font-size:20px">${transportInfo.icon}</div>
        <div style="flex:1; min-width:0;">
          <div id="nav-destination" style="font-weight:600; font-size:14px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:pointer;">${transportInfo.name} → ${this.navigationPoiName || 'Destination'}</div>
          <div id="nav-stats" style="font-size:12px; color:#555">${distStr} • ${etaStr}${hasNext ? ` • next ${remainingStops}` : ''}</div>
        </div>
        <div class="btn-group btn-group-sm" role="group" aria-label="Travel mode">
          <button class="btn btn-outline-secondary" id="nav-mode-drive" title="Drive">🚗</button>
          <button class="btn btn-outline-secondary" id="nav-mode-walk" title="Walk">🚶</button>
          <button class="btn btn-outline-secondary" id="nav-mode-cycle" title="Cycle">🚴</button>
        </div>
        ${showDetails ? '<button id="nav-details-btn" class="btn btn-sm btn-outline-secondary">Details</button>' : ''}
        <button id="nav-stop-btn" class="btn btn-sm btn-outline-danger">Stop</button>
      `
      panel.style.display = 'block'
      panel.querySelector('#nav-stop-btn')?.addEventListener('click', () => this.stopNavigation())
      if (showDetails) {
        panel.querySelector('#nav-details-btn')?.addEventListener('click', (e) => { e.stopPropagation(); this.showRoutePlanDetails() })
        panel.querySelector('#nav-destination')?.addEventListener('click', () => this.showRoutePlanDetails())
      } else {
        // If no details available, destination is not clickable
        const destEl = panel.querySelector('#nav-destination'); if (destEl) destEl.style.cursor = 'default'
      }
      const applyProfile = (p)=>{ 
        this.routePlanProfile = p; 
        try{ localStorage.setItem('route_plan_profile', p) }catch(_){}
        // Update button highlight immediately
        this.updateModeButtons(panel, p, 'nav')
        // Apply without opening details
        if (this.isNavigating) { 
          const prof = p; 
          const hasWaypoints = Array.isArray(this.navigationWaypoints) && this.navigationWaypoints.length > 0
          const dest = this.navigationDestination
          const poiId = this.navigationPoiId
          const poiName = this.navigationPoiName
          this.stopNavigation()
          if (hasWaypoints) {
            this.startPlannedRoute(prof)
          } else if (dest && poiId) {
            this.navigateToPOI(poiId, dest.lat, dest.lng, prof, poiName)
          }
        }
      }
      panel.querySelector('#nav-mode-drive')?.addEventListener('click', (e)=>{ e.stopPropagation(); applyProfile('driving-car') })
      panel.querySelector('#nav-mode-walk')?.addEventListener('click', (e)=>{ e.stopPropagation(); applyProfile('foot-walking') })
      panel.querySelector('#nav-mode-cycle')?.addEventListener('click', (e)=>{ e.stopPropagation(); applyProfile('cycling-regular') })
      // Highlight current selection
      this.updateModeButtons(panel, this.isNavigating ? this.navigationProfile : (this.routePlanProfile || 'driving-car'), 'nav')
      return
    }

    // Not navigating: show route plan if any
    if (this.routePlanPois.length > 0) {
      const listItems = this.routePlanPois.map((p, idx) => `
        <li class="d-flex justify-content-between align-items-center mb-1">
          <span class="text-truncate" style="max-width: 180px;">${idx + 1}. ${p.name}</span>
          <button class="btn btn-sm btn-link text-danger p-0" data-remove-poi="${p.id}" title="Remove">
            <i class="bi bi-x-circle"></i>
          </button>
        </li>
      `).join('')

      content.innerHTML = `
        <div style="font-size:20px">🧭</div>
        <div style="flex:1; min-width:0;">
          <div style="font-weight:600; font-size:14px;">Route plan (${this.routePlanPois.length})</div>
          <ul class="list-unstyled mb-0" style="max-height:120px; overflow:auto; font-size:12px;">${listItems}</ul>
        </div>
        <div class="d-flex flex-column gap-1">
          <button id="nav-go-btn" class="btn btn-sm btn-primary">Go</button>
          <button id="nav-clear-plan-btn" class="btn btn-sm btn-outline-secondary">Clear</button>
          <div class="btn-group btn-group-sm" role="group" aria-label="Travel mode">
            <button class="btn btn-outline-secondary" id="plan-mode-drive" title="Drive">🚗</button>
            <button class="btn btn-outline-secondary" id="plan-mode-walk" title="Walk">🚶</button>
            <button class="btn btn-outline-secondary" id="plan-mode-cycle" title="Cycle">🚴</button>
          </div>
        </div>
      `

      panel.style.display = 'block'
      panel.querySelector('#nav-go-btn')?.addEventListener('click', () => this.startPlannedRoute())
      panel.querySelector('#nav-clear-plan-btn')?.addEventListener('click', () => this.clearRoutePlan())
      const setDraftProfile = (p)=>{ this.routePlanProfile = p; try{ localStorage.setItem('route_plan_profile', p) }catch(_){ }; this.updateModeButtons(panel, p, 'plan') };
      panel.querySelector('#plan-mode-drive')?.addEventListener('click', (e)=>{ e.stopPropagation(); setDraftProfile('driving-car') })
      panel.querySelector('#plan-mode-walk')?.addEventListener('click', (e)=>{ e.stopPropagation(); setDraftProfile('foot-walking') })
      panel.querySelector('#plan-mode-cycle')?.addEventListener('click', (e)=>{ e.stopPropagation(); setDraftProfile('cycling-regular') })
      // Highlight current selection
      this.updateModeButtons(panel, (this.routePlanProfile || 'driving-car'), 'plan')
      panel.querySelectorAll('[data-remove-poi]')?.forEach(btn => {
        btn.addEventListener('click', (e) => {
          const id = e.currentTarget.getAttribute('data-remove-poi')
          this.removePoiFromRoute(id)
        })
      })
      return
    }

    panel.style.display = forceShow ? 'block' : 'none'
  }

  // Debounced reroute using the backend service
  async rerouteFromHere(currentLat, currentLng) {
    const now = Date.now()
    if (this._rerouteInFlight) return
    if (now - this._lastRerouteAt < 10000) return // 10s debounce
    if (!this.navigationDestination) return

    this._rerouteInFlight = true
    try {
      this.showRouteLoading('Recalculating route…')
      const body = {
        start: [currentLng, currentLat],
        end: [this.navigationDestination.lng, this.navigationDestination.lat],
        profile: this.navigationProfile
      }
      const response = await fetch('/api/routes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify(body)
      })
      const data = await response.json()
      if (response.ok && data.success) {
        this.navigationTotalDistance = data.distance
        this.navigationTotalDuration = data.duration
        this.hideRouteLoading()
        this.drawRealRoute(data.route, this.navigationPoiName || 'Destination', data.distance, data.duration, this.navigationProfile)
        this._lastRerouteAt = Date.now()
        // Immediately trim to remaining after reroute
        this.handleNavigationUpdate(currentLat, currentLng)
        this.showNotification('Route updated', 'info')
      }
    } catch (e) {
      console.warn('Reroute failed:', e?.message || e)
    } finally {
      this.hideRouteLoading()
      this._rerouteInFlight = false
    }
  }

  // Stop and clear navigation session
  stopNavigation() {
    this.isNavigating = false
    this.navigationPoiId = null
    this.navigationPoiName = null
    this.navigationDestination = null
    this.navigationRouteCoords = []
    this.navigationTotalDistance = 0
    this.navigationTotalDuration = 0
    this.navigationRemainingDistance = 0
    this.navigationEtaSeconds = 0
    this._lastRerouteAt = 0

    if (this.navigationRoute) {
      try { this.map.removeLayer(this.navigationRoute) } catch (_) {}
      this.navigationRoute = null
    }
    if (this.navigationStartMarker) {
      try { this.map.removeLayer(this.navigationStartMarker) } catch (_) {}
      this.navigationStartMarker = null
    }
    if (this.navigationEndMarker) {
      try { this.map.removeLayer(this.navigationEndMarker) } catch (_) {}
      this.navigationEndMarker = null
    }

    this.updateNavigationPanel() // hide

    // Remove persisted navigation cookie
    this.clearNavigationCookie()
  }

  // ---------- Navigation persistence (cookie) ----------
  persistNavigationStateToCookie() {
    try {
      const state = {
        poiId: this.navigationPoiId,
        poiName: this.navigationPoiName,
        destination: this.navigationDestination, // { lat, lng }
        profile: this.navigationProfile,
        updatedAt: Date.now()
      }
      const json = JSON.stringify(state)
      const maxAge = 60 * 60 * 24 * 7 // 7 days
      const secure = window.location.protocol === 'https:' ? '; secure' : ''
      document.cookie = `nav_state=${encodeURIComponent(json)}; path=/; max-age=${maxAge}; samesite=Lax${secure}`
    } catch (e) {
      // no-op
    }
  }

  loadNavigationStateFromCookie() {
    try {
      const match = document.cookie.split('; ').find(c => c.startsWith('nav_state='))
      if (!match) return null
      const raw = decodeURIComponent(match.split('=')[1] || '')
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed || !parsed.destination || typeof parsed.destination.lat !== 'number' || typeof parsed.destination.lng !== 'number') return null
      return parsed
    } catch (_) {
      return null
    }
  }

  clearNavigationCookie() {
    const secure = window.location.protocol === 'https:' ? '; secure' : ''
    document.cookie = `nav_state=; path=/; max-age=0; samesite=Lax${secure}`
  }

  // ---------- Route planning (multi-POI) ----------
  updateModeButtons(root, profile, scope) {
    const cfg = scope === 'nav' ? ['#nav-mode-drive', '#nav-mode-walk', '#nav-mode-cycle'] : ['#plan-mode-drive', '#plan-mode-walk', '#plan-mode-cycle']
    const map = {
      'driving-car': 0,
      'foot-walking': 1,
      'cycling-regular': 2
    }
    cfg.forEach((sel, idx) => {
      const btn = root.querySelector(sel)
      if (!btn) return
      if (map[profile] === idx) {
        btn.classList.remove('btn-outline-secondary')
        btn.classList.add('btn-secondary')
      } else {
        btn.classList.add('btn-outline-secondary')
        btn.classList.remove('btn-secondary')
      }
    })
  }
  showRoutePlanDetails() {
    // Build a simple overlay modal to list full journey with leg stats
    const existing = document.getElementById('route-plan-overlay')
    if (existing) existing.remove()
    const overlay = document.createElement('div')
    overlay.id = 'route-plan-overlay'
    overlay.style.position = 'fixed'
    overlay.style.left = '50%'
    overlay.style.top = '20px'
    overlay.style.transform = 'translateX(-50%)'
    overlay.style.zIndex = '10001'
    overlay.style.background = 'rgba(255,255,255,0.97)'
    overlay.style.boxShadow = '0 12px 28px rgba(0,0,0,0.2)'
    overlay.style.borderRadius = '12px'
    overlay.style.padding = '12px 16px'
    overlay.style.maxWidth = '90vw'
    overlay.style.minWidth = '260px'

    const items = this.routePlanPois.map((p, i) => {
      const current = (this.navigationWaypoints && this.currentWaypointIndex !== null && this.navigationWaypoints[this.currentWaypointIndex] === p.id)
      return `<li class=\"mb-2\"><div class=\"d-flex justify-content-between\"><span class=\"${current ? 'fw-bold' : ''}\">${i + 1}. ${p.name}</span><span id=\"leg-${i}-stats\" class=\"text-muted small\">--</span></div></li>`
    }).join('')
    overlay.innerHTML = `
      <div class="d-flex align-items-center justify-content-between mb-2">
        <div class="fw-semibold">Route plan (${this.routePlanPois.length})</div>
        <div class="d-flex gap-2">
          <button id="rp-apply" class="btn btn-sm btn-primary">Apply</button>
          <button id="rp-close" class="btn btn-sm btn-outline-secondary">Close</button>
        </div>
      </div>
      <div id="rp-summary" class="small text-muted mb-1">Calculating…</div>
      <ul id="rp-items" class="list-unstyled mb-2" style="max-height: 40vh; overflow:auto;">${items}</ul>
      <div class="text-muted small">Reorder stops, then Apply to recalc the route.</div>
    `
    document.body.appendChild(overlay)
    overlay.querySelector('#rp-close')?.addEventListener('click', () => overlay.remove())

    // Enable reordering (simple up/down buttons)
    const rebuild = () => {
      const currentOrder = [...this.routePlanPois]
      const list = overlay.querySelector('#rp-items')
      const withControls = currentOrder.map((p, i) => `
        <li class=\"mb-2\">
          <div class=\"d-flex align-items-center justify-content-between\">
            <div><span>${i + 1}. ${p.name}</span></div>
            <div class=\"btn-group btn-group-sm\" role=\"group\">
              <button class=\"btn btn-outline-secondary\" data-up=\"${i}\" ${i===0?'disabled':''}><i class=\"bi bi-chevron-up\"></i></button>
              <button class=\"btn btn-outline-secondary\" data-down=\"${i}\" ${i===currentOrder.length-1?'disabled':''}><i class=\"bi bi-chevron-down\"></i></button>
            </div>
          </div>
          <div class=\"text-muted small\" id=\"leg-${i}-stats\">--</div>
        </li>
      `).join('')
      list.innerHTML = withControls
      // Inject delete buttons per row
      try {
        const groups = list.querySelectorAll('.btn-group[role="group"]')
        groups.forEach((group, idx) => {
          const del = document.createElement('button')
          del.className = 'btn btn-outline-danger'
          del.setAttribute('data-del', String(idx))
          del.title = 'Remove'
          del.innerHTML = '<i class="bi bi-x"></i>'
          group.appendChild(del)
        })
      } catch (_) {}
      list.querySelectorAll('[data-up]')?.forEach(btn=>{
        btn.addEventListener('click', e=>{
          const i = parseInt(e.currentTarget.getAttribute('data-up'))
          if (i>0){ const t=currentOrder[i-1]; currentOrder[i-1]=currentOrder[i]; currentOrder[i]=t; this.routePlanPois=currentOrder; rebuild(); this.populatePlanLegStats() }
        })
      })
      list.querySelectorAll('[data-down]')?.forEach(btn=>{
        btn.addEventListener('click', e=>{
          const i = parseInt(e.currentTarget.getAttribute('data-down'))
          if (i<currentOrder.length-1){ const t=currentOrder[i+1]; currentOrder[i+1]=currentOrder[i]; currentOrder[i]=t; this.routePlanPois=currentOrder; rebuild(); this.populatePlanLegStats() }
        })
      })
      list.querySelectorAll('[data-del]')?.forEach(btn=>{
        btn.addEventListener('click', e=>{
          const i = parseInt(e.currentTarget.getAttribute('data-del'))
          if (!isNaN(i)) {
            currentOrder.splice(i, 1)
            this.routePlanPois = currentOrder
            rebuild()
            this.populatePlanLegStats()
          }
        })
      })
    }
    rebuild()

    // Apply changes: recalc route if navigating, or just update panel
    overlay.querySelector('#rp-apply')?.addEventListener('click', async () => {
      overlay.remove()
      if (this.isNavigating) {
        const prof = this.navigationProfile
        this.stopNavigation()
        await this.startPlannedRoute(prof)
      } else {
        this.updateNavigationPanel(true)
      }
    })

    // Kick off leg calculations
    this.populatePlanLegStats()
  }

  async populatePlanLegStats() {
    if (!this.userLocationMarker || this.routePlanPois.length === 0) return
    const profile = this.navigationProfile || 'driving-car'
    const userLat = this.userLocationMarker.getLatLng().lat
    const userLng = this.userLocationMarker.getLatLng().lng
    const points = [{ name: 'Start', lat: userLat, lng: userLng }, ...this.routePlanPois]
    let totalDistance = 0
    let totalDuration = 0

    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i]
      const b = points[i + 1]
      let legDistance = 0
      let legDuration = 0
      try {
        const res = await fetch('/api/routes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: JSON.stringify({ start: [a.lng, a.lat], end: [b.lng, b.lat], profile })
        })
        const data = await res.json()
        if (res.ok && data.success) {
          legDistance = data.distance || 0
          legDuration = data.duration || 0
        } else {
          throw new Error('segment failed')
        }
      } catch (_) {
        // Fallback to straight line + estimated duration
        legDistance = this.calculateDistance(a.lat, a.lng, b.lat, b.lng)
        legDuration = this.estimateDurationSeconds(legDistance, profile)
      }
      totalDistance += legDistance
      totalDuration += legDuration
      const el = document.getElementById(`leg-${i}-stats`)
      if (el) el.textContent = `${this.formatDistance(legDistance)} • ~${this.formatMinutes(legDuration)} min`
    }

    const summary = document.getElementById('rp-summary')
    if (summary) summary.textContent = `Total: ${this.formatDistance(totalDistance)} • ~${this.formatMinutes(totalDuration)} min`
  }

  estimateDurationSeconds(distanceMeters, profile) {
    // Rough speeds: driving 50km/h, cycling 15km/h, walking 5km/h
    const speedMps = {
      'driving-car': 50_000 / 3600,
      'cycling-regular': 15_000 / 3600,
      'foot-walking': 5_000 / 3600
    }[profile] || (50_000 / 3600)
    return Math.max(60, Math.round(distanceMeters / speedMps))
  }

  formatMinutes(seconds) { return Math.max(1, Math.round((seconds || 0) / 60)) }
  addPoiToRoute(poiId) {
    const marker = this.poiMarkers.find(m => m.poi && m.poi.id === poiId)
    if (!marker || !marker.poi || !Array.isArray(marker.poi.coordinates)) {
      this.showError('Unable to add this location to the route.')
      return
    }
    // Close the popup if open
    try { marker.closePopup() } catch (_) { try { this.map.closePopup() } catch (_) {} }
    const exists = this.routePlanPois.find(p => p.id === poiId)
    if (exists) {
      this.showNotification('Already in the route plan', 'info')
      return
    }
    const [lng, lat] = marker.poi.coordinates
    this.routePlanPois.push({ id: poiId, name: marker.poi.name, lat, lng })
    this.showSuccess(`Added ${marker.poi.name} to route plan`)
    this.updateNavigationPanel(true)
    // If details overlay is open, refresh it to include the newly added POI
    if (document.getElementById('route-plan-overlay')) {
      this.showRoutePlanDetails()
    }
  }

  removePoiFromRoute(poiId) {
    const before = this.routePlanPois.length
    this.routePlanPois = this.routePlanPois.filter(p => p.id !== poiId)
    if (this.routePlanPois.length < before) {
      this.showNotification('Removed from route plan', 'info')
    }
    this.updateNavigationPanel(true)
  }

  clearRoutePlan() {
    this.routePlanPois = []
    this.updateNavigationPanel()
  }

  async startPlannedRoute(profile = null) {
    if (!this.userLocationMarker) {
      this.showNotification('Please get your current location first. Click the "Find Me" button.', 'warning')
      return
    }
    if (this.routePlanPois.length === 0) {
      this.showNotification('Add locations to the route first', 'info')
      return
    }
    const userLat = this.userLocationMarker.getLatLng().lat
    const userLng = this.userLocationMarker.getLatLng().lng
    const mode = profile || this.routePlanProfile || this.navigationProfile || 'driving-car'

    // Build full coordinates list: current position followed by all POIs in order
    const coords = [[userLng, userLat], ...this.routePlanPois.map(p => [p.lng, p.lat])]

    try {
      this.showRouteLoading('Calculating route…')
      const response = await fetch('/api/routes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({ coordinates: coords, profile: mode })
      })
      const data = await response.json()
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to get route')

      // Success with full plan
      this.isNavigating = true
      this.navigationProfile = mode
      this.navigationWaypoints = this.routePlanPois.map(p => p.id)
      this.currentWaypointIndex = 0
      const finalPoi = this.routePlanPois[this.routePlanPois.length - 1]
      this.navigationPoiId = finalPoi.id
      this.navigationPoiName = finalPoi.name
      this.navigationDestination = { lat: finalPoi.lat, lng: finalPoi.lng }
      this.navigationTotalDistance = data.distance
      this.navigationTotalDuration = data.duration

      // Hide spinner right before drawing the route
      this.hideRouteLoading()
      this.drawRealRoute(data.route, finalPoi.name, data.distance, data.duration, mode)
      this.persistNavigationStateToCookie()
      this.updateNavigationPanel(true)
      this.handleNavigationUpdate(userLat, userLng)
    } catch (e) {
      // Fallback: compute per-leg and merge into a single polyline
      try {
        let combined = []
        let totalDistance = 0
        let totalDuration = 0
        const points = [[userLng, userLat], ...this.routePlanPois.map(p => [p.lng, p.lat])]
        const failedSegments = []
        for (let i = 0; i < points.length - 1; i++) {
          const start = points[i]     // [lng, lat]
          const end = points[i + 1]  // [lng, lat]
          let segOk = false
          try {
            const res = await fetch('/api/routes', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
              },
              body: JSON.stringify({ start: start, end: end, profile: mode })
            })
            const seg = await res.json()
            if (res.ok && seg.success) {
              const coords = this.extractRouteLatLng(seg.route)
              if (coords.length > 0) {
                combined = combined.length > 0 ? combined.concat(coords.slice(1)) : coords
                totalDistance += (seg.distance || 0)
                totalDuration += (seg.duration || 0)
                segOk = true
              }
            }
          } catch (_) {}

          if (!segOk) {
            // Fallback to straight segment for this leg
            const a = [start[1], start[0]] // [lat,lng]
            const b = [end[1], end[0]]     // [lat,lng]
            combined = combined.length > 0 ? combined.concat([b]) : [a, b]
            totalDistance += this.calculateDistance(a[0], a[1], b[0], b[1])
            // Rough duration unknown; leave as is
            failedSegments.push(i)
          }
        }

        if (combined.length > 1) {
          // Build a single polyline from merged segments
          try { if (this.navigationRoute) this.map.removeLayer(this.navigationRoute) } catch (_) {}
          // Hide spinner right before drawing the route
          this.hideRouteLoading()
          this.navigationRouteCoords = combined
          this.navigationRoute = L.polyline(combined, {
            color: '#28a745',
            weight: 6,
            opacity: 0.9,
            smoothFactor: 1
          }).addTo(this.map)

          // Start/end markers
          const startLatLng = combined[0]
          const endLatLng = combined[combined.length - 1]
          try { if (this.navigationStartMarker) this.map.removeLayer(this.navigationStartMarker) } catch (_) {}
          try { if (this.navigationEndMarker) this.map.removeLayer(this.navigationEndMarker) } catch (_) {}
          this.navigationStartMarker = L.marker(startLatLng, { icon: L.divIcon({ className: 'navigation-start-marker', html: '<div class="navigation-start-icon">🚀</div>', iconSize: [24, 24] }) }).addTo(this.map)
          this.navigationEndMarker = L.marker(endLatLng, { icon: L.divIcon({ className: 'navigation-end-marker', html: '<div class="navigation-end-icon">🎯</div>', iconSize: [24, 24] }) }).addTo(this.map)

          // Session data
          this.isNavigating = true
          this.navigationProfile = mode
          this.navigationWaypoints = this.routePlanPois.map(p => p.id)
          this.currentWaypointIndex = 0
          const finalPoi = this.routePlanPois[this.routePlanPois.length - 1]
          this.navigationPoiId = finalPoi.id
          this.navigationPoiName = finalPoi.name
          this.navigationDestination = { lat: finalPoi.lat, lng: finalPoi.lng }
          this.navigationTotalDistance = totalDistance
          this.navigationTotalDuration = totalDuration

          // Fit bounds and show panel
          this.map.fitBounds(L.latLngBounds(combined), { padding: [50, 50] })
          this.persistNavigationStateToCookie()
          this.updateNavigationPanel(true)
          this.handleNavigationUpdate(userLat, userLng)
          if (failedSegments.length > 0) {
            this.showNotification('Some legs used direct lines due to routing limits', 'warning')
          }
        } else {
          throw new Error('No route segments')
        }
      } catch (err) {
        this.hideRouteLoading()
        this.showError('Could not start planned route')
      }
    } finally {
      // Safety: ensure spinner is hidden
      this.hideRouteLoading()
    }
  }

  goToNextWaypoint() {
    if (!Array.isArray(this.navigationWaypoints) || this.currentWaypointIndex === null) return
    if (this.currentWaypointIndex >= this.navigationWaypoints.length - 1) return
    const nextIndex = this.currentWaypointIndex + 1
    const nextId = this.navigationWaypoints[nextIndex]
    const marker = this.poiMarkers.find(m => m.poi && m.poi.id === nextId)
    if (!marker || !marker.poi || !Array.isArray(marker.poi.coordinates)) return
    const [lng, lat] = marker.poi.coordinates
    this.currentWaypointIndex = nextIndex
    this.navigateToPOI(nextId, lat, lng, this.navigationProfile, marker.poi.name)
  }

  formatDistance(meters) {
    if (meters < 1000) {
      return `${Math.round(meters)}m`
    } else {
      return `${(meters / 1000).toFixed(1)}km`
    }
  }

  // Decode encoded polyline from OpenRouteService
  decodePolyline(encoded) {
    const poly = []
    let index = 0, len = encoded.length
    let lat = 0, lng = 0

    while (index < len) {
      let shift = 0, result = 0

      do {
        let b = encoded.charCodeAt(index++) - 63
        result |= (b & 0x1f) << shift
        shift += 5
      } while (result >= 0x20)

      let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1))
      lat += dlat

      shift = 0
      result = 0

      do {
        let b = encoded.charCodeAt(index++) - 63
        result |= (b & 0x1f) << shift
        shift += 5
      } while (result >= 0x20)

      let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1))
      lng += dlng

      poly.push([lng / 1e5, lat / 1e5])
    }

    return poly
  }

  // More robust polyline decoder with better error handling
  decodePolylineRobust(encoded) {
    if (!encoded || typeof encoded !== 'string') {
      console.error('Invalid polyline input:', encoded)
      return []
    }

    const poly = []
    let index = 0, len = encoded.length
    let lat = 0, lng = 0

    try {
      while (index < len) {
        let shift = 0, result = 0

        do {
          if (index >= len) break
          let b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (result >= 0x20)

        let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1))
        lat += dlat

        shift = 0
        result = 0

        do {
          if (index >= len) break
          let b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (result >= 0x20)

        let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1))
        lng += dlng

        // Validate coordinates before adding
        const finalLat = lat / 1e5
        const finalLng = lng / 1e5
        
        if (finalLat >= -90 && finalLat <= 90 && finalLng >= -180 && finalLng <= 180) {
          poly.push([finalLng, finalLat])
        } else {
          console.warn('Invalid coordinate detected:', [finalLng, finalLat])
        }
      }
    } catch (error) {
      console.error('Error during polyline decoding:', error)
      return []
    }

    console.log('Successfully decoded polyline with', poly.length, 'coordinates')
    return poly
  }

  // Auto-detect precision for relative polyline (1e5 vs 1e6)
  decodePolylineAuto(encoded) {
    if (!encoded || typeof encoded !== 'string') return []
    // Try standard 1e5 first
    const decoded1e5 = this.decodePolylineRobust(encoded).map(([lng, lat]) => [lng, lat])
    if (this.isLikelyValidLatLngArray(decoded1e5)) return decoded1e5

    // Fallback: attempt 1e6 precision decoding
    const decoded1e6 = this.decodePolylineWithPrecision(encoded, 1e6)
    if (this.isLikelyValidLatLngArray(decoded1e6)) return decoded1e6

    // As last resort, return standard
    return decoded1e5
  }

  // Decode with custom precision divisor (e.g., 1e6)
  decodePolylineWithPrecision(encoded, precisionDivisor) {
    const poly = []
    let index = 0, len = encoded.length
    let lat = 0, lng = 0
    try {
      while (index < len) {
        let shift = 0, result = 0
        do {
          if (index >= len) break
          let b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (result >= 0x20)
        let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1))
        lat += dlat

        shift = 0
        result = 0
        do {
          if (index >= len) break
          let b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (result >= 0x20)
        let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1))
        lng += dlng

        const finalLat = lat / precisionDivisor
        const finalLng = lng / precisionDivisor
        if (finalLat >= -90 && finalLat <= 90 && finalLng >= -180 && finalLng <= 180) {
          poly.push([finalLng, finalLat])
        }
      }
    } catch (_) { return [] }
    return poly
  }

  // Basic sanity-check for coordinates
  isLikelyValidLatLngArray(coords) {
    if (!Array.isArray(coords) || coords.length < 2) return false
    // Sample a few points
    const sample = [coords[0], coords[Math.floor(coords.length / 2)], coords[coords.length - 1]]
    return sample.every((c) => Array.isArray(c) && c.length === 2 && c[1] >= -90 && c[1] <= 90 && c[0] >= -180 && c[0] <= 180)
  }

  // Decode polyline and convert to absolute coordinates
  decodePolylineAbsolute(encoded, startLat, startLng) {
    if (!encoded || typeof encoded !== 'string') {
      console.error('Invalid polyline input:', encoded)
      return []
    }

    const poly = []
    let index = 0, len = encoded.length
    let lat = startLat, lng = startLng

    try {
      while (index < len) {
        let shift = 0, result = 0

        do {
          if (index >= len) break
          let b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (result >= 0x20)

        let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1))
        lat += dlat / 1e5

        shift = 0
        result = 0

        do {
          if (index >= len) break
          let b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (result >= 0x20)

        let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1))
        lng += dlng / 1e5

        // Validate coordinates before adding
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          poly.push([lng, lat])
        } else {
          console.warn('Invalid coordinate detected:', [lng, lat])
        }
      }
    } catch (error) {
      console.error('Error during polyline decoding:', error)
      return []
    }

    console.log('Successfully decoded absolute polyline with', poly.length, 'coordinates')
    console.log('Route starts at:', [startLat, startLng])
    console.log('Route ends at:', [lat, lng])
    return poly
  }

  // Create a curved route between two points (simulates road routing)
  createCurvedRoute(startLat, startLng, endLat, endLng) {
    const coordinates = []
    
    // Add start point
    coordinates.push([startLat, startLng])
    
    // Calculate midpoint and add some curve
    const midLat = (startLat + endLat) / 2
    const midLng = (startLng + endLng) / 2
    
    // Add some offset to create a curve (simulates road routing)
    const latDiff = Math.abs(endLat - startLat)
    const lngDiff = Math.abs(endLng - startLng)
    
    // Create 3-5 intermediate points with slight curves
    const numPoints = Math.max(3, Math.min(5, Math.floor(Math.sqrt(latDiff * latDiff + lngDiff * lngDiff) * 1000)))
    
    for (let i = 1; i < numPoints; i++) {
      const t = i / numPoints
      const lat = startLat + (endLat - startLat) * t
      const lng = startLng + (endLng - startLng) * t
      
      // Add slight curve by offsetting perpendicular to the line
      const curveOffset = 0.0001 * Math.sin(Math.PI * t) // Small curve
      const perpLat = lat + curveOffset
      const perpLng = lng + curveOffset
      
      coordinates.push([perpLat, perpLng])
    }
    
    // Add end point
    coordinates.push([endLat, endLng])
    
    return coordinates
  }

  setupEventListeners() {
    // Make map controller globally accessible for popup buttons
    window.mapController = this
    
    // Handle window resize
    window.addEventListener('resize', () => {
      if (this.map) {
        this.map.invalidateSize()
      }
      
      // Update alert container positioning on resize (for mobile orientation changes)
      const alertContainer = document.getElementById('alert-container')
      if (alertContainer) {
        if (window.innerWidth <= 768) {
          alertContainer.className = 'position-fixed'
          alertContainer.style.top = '10px'
          alertContainer.style.left = '10px'
          alertContainer.style.right = '10px'
          alertContainer.style.marginTop = '0'
        } else {
          alertContainer.className = 'position-fixed top-0 start-50 translate-middle-x'
          alertContainer.style.top = ''
          alertContainer.style.left = ''
          alertContainer.style.right = ''
          alertContainer.style.marginTop = '20px'
        }
      }
    })
  }

  // ---------- Custom POI creation ----------
  async showCustomPoiForm(lat, lng) {
    try { this.map.closePopup() } catch (_) {}
    const popup = L.popup({ closeButton: true, autoClose: true, closeOnClick: true })
      .setLatLng([lat, lng])
      .setContent(`
        <div style="min-width: 260px;">
          <div class="mb-2 fw-semibold">Add custom place</div>
          <div class="mb-2">
            <label class="form-label small mb-1">Name</label>
            <input id="custom-poi-name" class="form-control form-control-sm" placeholder="E.g., Coffee stop" required />
          </div>
          <div class="row g-2 mb-2">
            <div class="col-6">
              <label class="form-label small mb-1">Latitude</label>
              <input id="custom-poi-lat" class="form-control form-control-sm" value="${lat.toFixed(6)}" readonly />
            </div>
            <div class="col-6">
              <label class="form-label small mb-1">Longitude</label>
              <input id="custom-poi-lng" class="form-control form-control-sm" value="${lng.toFixed(6)}" readonly />
            </div>
          </div>
          <div class="row g-2 mb-3">
            <div class="col-6">
              <label class="form-label small mb-1">City</label>
              <input id="custom-poi-city" class="form-control form-control-sm" placeholder="Optional" />
            </div>
            <div class="col-6">
              <label class="form-label small mb-1">Country</label>
              <input id="custom-poi-country" class="form-control form-control-sm" placeholder="Optional" />
            </div>
          </div>
          <div class="d-flex gap-2">
            <button id="custom-poi-add" class="btn btn-sm btn-primary">Save place</button>
            <button id="custom-poi-cancel" class="btn btn-sm btn-outline-secondary">Cancel</button>
          </div>
        </div>
      `)
      .openOn(this.map)

    const root = popup.getElement()
    if (!root) return

    // Wire up buttons
    const onCancel = (ev) => { ev.preventDefault(); try { popup.remove() } catch (_) {} }
    root.querySelector('#custom-poi-cancel')?.addEventListener('click', onCancel)
    root.querySelector('#custom-poi-add')?.addEventListener('click', (ev) => {
      ev.preventDefault()
      this.addCustomPoiFromPopup(lat, lng, popup)
    })

    // Attempt reverse geocoding to prefill city/country
    try {
      const meta = await this.reverseGeocode(lat, lng)
      const cityInput = root.querySelector('#custom-poi-city')
      const countryInput = root.querySelector('#custom-poi-country')
      if (cityInput && !cityInput.value && meta.city) cityInput.value = meta.city
      if (countryInput && !countryInput.value && meta.country) countryInput.value = meta.country
    } catch (_) { /* ignore */ }
  }

  async reverseGeocode(lat, lng) {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&zoom=10&addressdetails=1`
    const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
    if (!res.ok) return { city: null, country: null }
    const data = await res.json()
    const addr = data && data.address ? data.address : {}
    const city = addr.city || addr.town || addr.village || addr.hamlet || null
    const country = addr.country || null
    return { city, country }
  }

  addCustomPoiFromPopup(lat, lng, popupRef) {
    const root = popupRef && popupRef.getElement ? popupRef.getElement() : document
    const nameEl = root.querySelector('#custom-poi-name')
    const cityEl = root.querySelector('#custom-poi-city')
    const countryEl = root.querySelector('#custom-poi-country')
    const name = (nameEl?.value || '').trim()
    const city = (cityEl?.value || '').trim()
    const country = (countryEl?.value || '').trim()
    if (!name) {
      this.showNotification('Please enter a name for the place', 'warning')
      try { nameEl?.focus() } catch (_) {}
      return
    }

    // Create custom POI object and marker
    // Persist to backend then add marker
    const save = async () => {
      const body = {
        name,
        city,
        country,
        latitude: lat,
        longitude: lng,
        description: 'Custom place'
      }
      try {
        // Show a saving overlay while persisting
        this.showRouteLoading('Saving place…')
        const res = await fetch('/api/v1/point_of_interests', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          },
          body: JSON.stringify(body)
        })
        const data = await res.json()
        if (!res.ok || data.success !== true) {
          throw new Error(data.error || 'Failed to save custom POI')
        }
        const id = (data && data.data && data.data.id) || data.id
        if (!id) {
          throw new Error('Invalid response from server')
        }
        const poi = {
          id,
          name,
          description: 'Custom place',
          city,
          country,
          check_in_radius: 100,
          coordinates: [lng, lat],
          is_private: true
        }
        // Create a regular marker using existing rendering (purple color for private)
        this.addPoiMarker(poi)
        // Inform user; do not auto-add to route plan
        this.showSuccess(`Saved ${name}. You can add it to your route from the popup.`)
        try { popupRef.remove() } catch (_) {}
      } catch (err) {
        console.error('Failed to save custom POI:', err)
        this.showError('Failed to save this place. Please try again.')
      } finally {
        this.hideRouteLoading()
      }
    }
    save()
  }
}